import React, { useContext, useEffect, useState } from 'react';
import { bool, shape } from 'prop-types';
import classNames from 'classnames';
import {
  number as numberType,
  params,
  shape as shapeType,
  string as stringType,
  useDataModel
} from '@thd-nucleus/data-sources';
import { ExperienceContext, useStore } from '@thd-nucleus/experience-context';
import { useLayoutGroupProps } from '@thd-olt-component-react/layout-manager';
import { useLifeCycleEventBus } from '@thd-olt-functional/utils';
import { IntroOffer } from './subcomponents/intro-offer/intro-offer.component';
import { PaymentEstimator } from './subcomponents/payment-estimator/payment-estimator.component';
import { OffersForYou } from './subcomponents/offers-for-you/offers-for-you.component';
import { loadEstimatorScript } from './utils';
import './consumer-card-messaging.style.scss';

const isValidItemId = (itemId) => /^\d{9}$/.test(itemId);
const isValidBundlePrice = (bundlePrice) => {
  return Boolean(
    bundlePrice?.length
  );
};
const isValidProps = (props) => {
  const { itemId, bundlePrice } = props;
  return isValidItemId(itemId) || isValidBundlePrice(bundlePrice);
};

const isValidData = (data) => {
  return typeof data?.product?.pricing?.value === 'number';
};

export const ConsumerCardMessaging = (props) => {
  const { channel } = useContext(ExperienceContext);
  const { isLocalized } = useStore();
  const { paymentEstimatorVersion = 'v1.0.33' } = useLayoutGroupProps({ props });
  const [paymentEstimatorLoaded, setPaymentEstimatorLoaded] = useState(false);

  const {
    itemId,
    bundlePrice,
    isAppliance,
    isFinancePromoEligible,
    clsRemediation
  } = props;
  const { configuredPrice } = useLifeCycleEventBus('configurator.configurator_pricing_event');
  const hasValidBundlePrice = isValidBundlePrice(bundlePrice);
  const hasValidProps = isValidProps(props);

  const { data, loading } = useDataModel('product', {
    variables: { itemId },
    skip: !hasValidProps || hasValidBundlePrice
  });

  useEffect(() => {
    loadEstimatorScript(paymentEstimatorVersion, () => setPaymentEstimatorLoaded(true), channel === 'mobile');
  }, []);

  const hasValidData = isValidData(data) || hasValidBundlePrice;
  const hasDataFullyLoaded = paymentEstimatorLoaded && isLocalized && !loading;

  if (!hasValidProps) {
    return null;
  }

  if (!hasValidData) {
    const { placeholders, preservePlaceholders } = clsRemediation;
    if (!placeholders) {
      return null;
    }
    if (hasDataFullyLoaded && !preservePlaceholders) {
      return null;
    }
  }

  const consumerCardClasses = classNames({
    'consumer-card-messaging--hidden': !hasValidData || !hasDataFullyLoaded
  });

  const isProductAppliance = data?.product?.identifiers?.productType === 'MAJOR_APPLIANCE';
  const value = configuredPrice?.pricing?.value || data?.product?.pricing?.value;
  const showPaymentEstimator = !hasValidBundlePrice && value >= 299.00;
  const showIntroOffer = !hasValidBundlePrice && !showPaymentEstimator && value >= 99.00;

  return (
    <div className={consumerCardClasses}>
      {hasValidBundlePrice && (
        <OffersForYou
          price={`${bundlePrice}`}
          isMobile={channel === 'mobile'}
          isAppliance={isAppliance}
          paymentEstimatorLoaded={paymentEstimatorLoaded}
        />
      )}
      {showPaymentEstimator && (
        <PaymentEstimator
          price={`${value}`}
          isMobile={channel === 'mobile'}
          isAppliance={isProductAppliance}
          isHDHome={isFinancePromoEligible}
          paymentEstimatorLoaded={paymentEstimatorLoaded}
        />
      )}
      {showIntroOffer && (
        <IntroOffer
          mobile={channel === 'mobile'}
          paymentEstimatorLoaded={paymentEstimatorLoaded}
        />
      )}
    </div>
  );
};

ConsumerCardMessaging.displayName = 'ConsumerCardMessaging';

ConsumerCardMessaging.dataModel = {
  product: params({
    itemId: stringType().isRequired()
  }).shape({
    pricing: params({ storeId: stringType() }).shape({
      value: numberType()
    }),
    identifiers: shapeType({
      productType: stringType()
    })
  })
};

const checkRequiredProps = (props) => {
  if (!props.itemId && !props.bundlePrice) {
    return new Error('The \'itemId\' or \'bundlePrice\' is required by the consumer-card-messaging component.');
  }
  return null;
};

ConsumerCardMessaging.propTypes = {
  itemId: checkRequiredProps,
  bundlePrice: checkRequiredProps,
  isAppliance: bool,
  isFinancePromoEligible: bool,
  clsRemediation: shape({
    placeholders: bool,
    preservePlaceholders: bool
  })
};

ConsumerCardMessaging.defaultProps = {
  itemId: null,
  bundlePrice: null,
  isAppliance: false,
  isFinancePromoEligible: false,
  clsRemediation: {
    placeholders: false,
    preservePlaceholders: false
  }
};
